import { BasicFile, DatePickerAPI, DynamicFieldsAPI, FormAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ScheduleWaitingListData } from '../../app/types';
import { AdditionalInformation, AdditionalInformationValues, CurrentPatientSelected, DateRange, DocumentUploadScheduleRuleResponse, EstablishmentGroupLocalizationModel, HealthInsuranceCardProps, PriorLocation, ProcedureModel, PseudonymWaitingListData, QuizMetadata, Suggestions } from './pseudonym-interfaces';
import PseudonymSuggestionListModel from './pseudonym-suggestion/pseudonym-suggestion-list-model';

export default class PseudonymAppointmentModel {

  // General
  static integratedScheduleSequence: number;
  static isQuizAvailable: boolean;
  static hasMetadataQuiz: boolean;

  // Step 1 - Patient Informations
  static stepOne = {
    currentPatientSelected: {} as CurrentPatientSelected,
    healthInsurance: {} as HealthInsuranceCardProps,
    priorLocation: {} as PriorLocation,
  };

  // Step 2 - Procedures
  static stepTwo = {
    clearFields: () => { },
    hasExam: false as boolean,
    hasProcedureChange: false as boolean,
    isOnlyConsultation: false as boolean,
    procedures: [] as Array<ProcedureModel>,
  };

  // Step 3 - Additional Information
  static stepThree = {
    additionalInformation: {} as AdditionalInformation,
    additionalInformationValues: {} as AdditionalInformationValues,
    loadAdditionalInformation: () => { },
    saveAdditionalInformation: () => { },
  };

  // Step 4 - Quiz
  static stepFour = {
    populateQuizTab: (): Promise<void> => { return Promise.resolve() },
    quizDynamicFieldsApi: {} as DynamicFieldsAPI,
    quizDynamicMetada: {} as QuizMetadata,
    quizFormApi: {} as FormAPI,
  };

  // Step 5 - Location
  static stepFive = {
    loadLocation: () => {},
    location: {} as EstablishmentGroupLocalizationModel,
  };

  // Step 6 - Schedule Period
  static stepSix = {
    allowSelectDesiredDate: false as boolean,
    availableDays: [],
    datePickerScheduleRange: {} as DatePickerAPI,
    desiredDateCustomMessage: '',
    panelNotificationSchedulePeriodApi: {} as PanelAPI,
    selectedDateRange: {} as DateRange,
    verifyAvailableCalendarDays: () => { },
  };

  // Step 7 - Suggestions
  static stepSeven = {
    isLastSuggestionDenied: false as boolean,
    panelLoaderConfirmationApi: {} as PanelAPI,
    populateSuggestionTab: () => { },
    suggestionListModel: {} as PseudonymSuggestionListModel,
    suggestions: {} as Suggestions,
    unSelectAllCards: () => { },
    verifyIfCanHandleConfirmSuggestion: () => { }
  }

  // Step 8 - Summary
  static stepEight = {
    hasSelectedConsultations: false,
    hasSelectedExams: false,
    hasSelectedSevices: false,
    hasWaitingList: false,
    panelLoaderConfirmationApi: {} as PanelAPI,
    populateSummaryCardsWithSelectedItems: () => { }
  }

  // Step 7.5 - Documents
  static stepDocumentsUpload = {
    documentsToUpload: [] as DocumentUploadScheduleRuleResponse[],
    hasFilesToUpload: false as boolean,
    memoryFiles: Object as unknown as Map<string, Blob>,
    populateFileUploaderItems: (): Promise<void> => { return Promise.resolve() },
    uploadedFiles: [] as Array<BasicFile>,
  };

  // Waiting List
  static waitingList = {
    pseudonymWaitingListData: {} as PseudonymWaitingListData,
    scheduleWaitingListDataArray: [] as Array<ScheduleWaitingListData>,
  };

  static clear(): void {
    // Default Values
    const CURRENT_PRIOR_LOCATION_SELECTED_DEFAULT_VALUE = { description: '', isVisible: false, sequenceNumber: 0 } as PriorLocation;
    const CURRENT_PATIENT_SELECTED_DEFAULT_VALUE = { id: 0, name: '' } as CurrentPatientSelected;
    const HEALTH_INSURANCE_DEFAULT_VALUE = {
      cardId: '',
      categoryDesc: '',
      categoryId: '',
      healthCardNumber: '',
      healthInsuranceDesc: '',
      healthInsuranceId: 0,
      isNewItemButton: false,
      isPrivate: false,
      planDesc: '',
      planId: '',
      walletExpiryDate: '',
    } as HealthInsuranceCardProps;

    this.integratedScheduleSequence = 0;
    this.isQuizAvailable = false;
    this.clearStepOne(CURRENT_PATIENT_SELECTED_DEFAULT_VALUE, HEALTH_INSURANCE_DEFAULT_VALUE, CURRENT_PRIOR_LOCATION_SELECTED_DEFAULT_VALUE);
    this.clearStepTwo();
    this.clearStepThree();
    this.clearStepFour();
    this.clearStepFive();
    this.clearStepSix();
    this.clearStepSeven();
    this.clearStepEight();
    this.clearDocumentsUploadStep();
    this.clearWaitingList(CURRENT_PATIENT_SELECTED_DEFAULT_VALUE, HEALTH_INSURANCE_DEFAULT_VALUE);
  }

  // Step 1 - Patient Informations
  private static clearStepOne(currentPatientSelected: CurrentPatientSelected, healthInsurance: HealthInsuranceCardProps, priorLocation: PriorLocation): void {
    this.stepOne = {
      currentPatientSelected: currentPatientSelected,
      healthInsurance: healthInsurance,
      priorLocation: priorLocation,
    };
  }

  // Step 2 - Procedures
  private static clearStepTwo(): void {
    this.stepTwo = {
      clearFields: () => { },
      hasExam: false,
      hasProcedureChange: false,
      isOnlyConsultation: false,
      procedures: [],
    };
  }

  // Step 3 - Additional Information
  private static clearStepThree(): void {
    const ADDITIONAL_INFORMATION_DEFAULT_VALUE = { heightInCentimeters: 0, saveAdditionalInfo: false, weight: 0 } as AdditionalInformation;

    this.stepThree = {
      additionalInformation: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
      additionalInformationValues: {
        newValue: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
        oldValue: ADDITIONAL_INFORMATION_DEFAULT_VALUE,
      },
      loadAdditionalInformation: () => { },
      saveAdditionalInformation: () => { },
    };
  }

  // Step 4 - Quiz
  private static clearStepFour(): void {
    this.stepFour = {
      populateQuizTab: (): Promise<void> => { return Promise.resolve() },
      quizDynamicFieldsApi: {} as DynamicFieldsAPI,
      quizDynamicMetada: {} as QuizMetadata,
      quizFormApi: {} as unknown as FormAPI,
    };
  }

  // Step 5 - Location
  private static clearStepFive(): void {
    this.stepFive = {
      loadLocation: () => {},
      location: { dsGrupoEstab: '', nrSequencia: 0 },
    };
  }

  // Step 6 - Schedule Period
  private static clearStepSix(): void {
    this.stepSix = {
      allowSelectDesiredDate: false,
      availableDays: [],
      datePickerScheduleRange: {} as DatePickerAPI,
      desiredDateCustomMessage: '',
      panelNotificationSchedulePeriodApi: {} as PanelAPI,
      selectedDateRange: { end: '', start: '' },
      verifyAvailableCalendarDays: () => { },
    };
  }

  // Step 7 - Suggestions
  private static clearStepSeven(): void {
    this.stepSeven = {
      isLastSuggestionDenied: false,
      panelLoaderConfirmationApi: {} as PanelAPI,
      populateSuggestionTab: () => { },
      suggestionListModel: {} as PseudonymSuggestionListModel,
      suggestions: {
        blockedSuggestionList: [],
        isEnabledToScheduleWaitingList: false,
        isShowNotificationWaitingList: false,
        suggestionGroups: [],
        suggestionList: [],
      },
      unSelectAllCards: () => {},
      verifyIfCanHandleConfirmSuggestion: () => {},
    };
  }

  // Step 8 - Summary
  private static clearStepEight(): void {
    this.stepEight = {
      hasSelectedConsultations: false,
      hasSelectedExams: false,
      hasSelectedSevices: false,
      hasWaitingList: false,
      panelLoaderConfirmationApi: {} as PanelAPI,
      populateSummaryCardsWithSelectedItems: () => {}
    };
  }

  // Waiting List
  private static clearWaitingList(currentPatientSelected: CurrentPatientSelected, healthInsurance: HealthInsuranceCardProps): void {
    this.waitingList = {
      pseudonymWaitingListData: {
        availablePeriod: [],
        dependencies: {
          calendarFinalDateInitialRange: '',
          currentPatientSelected: currentPatientSelected,
          healthInsuranceData: healthInsurance,
        },
        nrSeqAgeint: 0,
        nrSeqAgeintItem: '',
        procedureList: [],
        urgencyReason: { dsReason: '', nrSequence: 0 }
      },
      scheduleWaitingListDataArray: [],
    };
  }

  private static clearDocumentsUploadStep(): void {
    this.stepDocumentsUpload = {
      documentsToUpload: [] as DocumentUploadScheduleRuleResponse[],
      hasFilesToUpload: false as boolean,
      memoryFiles: Object as unknown as Map<string, Blob>,
      populateFileUploaderItems: (): Promise<void> => { return Promise.resolve() },
      uploadedFiles: [] as Array<BasicFile>,
    }
  }
}
